import { FaroAvatar, FaroTable } from '@faro/design-system';
import { AgenticArtifactReference } from '@faro/study-space-service-model';
import { UserInfo } from '@faro/user-management-model';
import { KeyboardEvent, MouseEvent, ReactNode, useMemo, useRef } from 'react';
import useSortableTable from '../../package/react-hooks/useSortableTable';
import { getFormattedDateTime } from '../../util/getFormattedDateTime';
import { PublishedAgenticArtifactMenu } from './PublishedAgenticArtifactMenu';
import styles from './PublishedAgenticArtifactTable.module.scss';
import { PublishedAgenticArtifactTableSkeleton } from './PublishedAgenticArtifactTable.skeleton';

export interface PublishedAgenticArtifactTableRow {
    artifact: AgenticArtifactReference;
    publishedByUser?: UserInfo;
}

export interface PublishedAgenticArtifactTableProps {
    spaceId: string;
    rows: PublishedAgenticArtifactTableRow[];
    onRename(artifact: AgenticArtifactReference): void;
    onDelete(artifact: AgenticArtifactReference): void;
    onRowClick?(row: PublishedAgenticArtifactTableRow): void;
}

export function PublishedAgenticArtifactTable(props: PublishedAgenticArtifactTableProps): ReactNode {
    const { spaceId, rows, onRename, onDelete, onRowClick } = props;

    const sortableRows = useMemo(
        () =>
            rows.map(row => ({
                ...row,
                studyRevisionDateMs: new Date(row.artifact.studyRevisionDate).valueOf(),
                publishedDateMs: new Date(row.artifact.publishedDate).valueOf(),
            })),
        [rows]
    );

    const {
        items: sortedRows,
        sortDataBy,
        sortIconBy,
    } = useSortableTable(sortableRows, {
        keys: ['publishedDateMs'],
        direction: 'desc',
    });

    if (rows.length === 0) {
        return (
            <p>
                This study space currently has no published optimization reports. Reports can be published here from a
                project in Protocol Optimization.
            </p>
        );
    }

    return (
        <FaroTable
            className={styles.table}
            appearance="bordered-minimally-interactive"
            data-automation-id="optimizationReportsTable"
            data-testid="optimizationReportsTable"
        >
            <thead>
                <tr>
                    <th onClick={() => sortDataBy(['artifact.name'])} data-testid="optimizationReportsTableHeader-name">
                        <div className={styles.headerContent}>Project Name {sortIconBy('artifact.name')}</div>
                    </th>
                    <th
                        onClick={() => sortDataBy(['artifact.studyDefinitionUsed'])}
                        data-testid="optimizationReportsTableHeader-studyDefinitionUsed"
                    >
                        <div className={styles.headerContent}>
                            Study Definition Used {sortIconBy('artifact.studyDefinitionUsed')}
                        </div>
                    </th>
                    <th
                        onClick={() => sortDataBy(['studyRevisionDateMs'])}
                        data-testid="optimizationReportsTableHeader-revision"
                    >
                        <div className={styles.headerContent}>Revision {sortIconBy('studyRevisionDateMs')}</div>
                    </th>
                    <th
                        onClick={() => sortDataBy(['publishedDateMs'])}
                        data-testid="optimizationReportsTableHeader-publishedDate"
                    >
                        <div className={styles.headerContent}>Published Date {sortIconBy('publishedDateMs')}</div>
                    </th>
                    <th data-testid="optimizationReportsTableHeader-publishedBy" />
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {sortedRows.map(row => (
                    <PublishedAgenticArtifactRow
                        key={`${row.artifact.artifactId}-${row.artifact.version}`}
                        spaceId={spaceId}
                        row={row}
                        onRename={onRename}
                        onDelete={onDelete}
                        onRowClick={onRowClick}
                    />
                ))}
            </tbody>
        </FaroTable>
    );
}

PublishedAgenticArtifactTable.Skeleton = PublishedAgenticArtifactTableSkeleton;

interface PublishedAgenticArtifactRowProps {
    spaceId: string;
    row: PublishedAgenticArtifactTableRow;
    onRename(artifact: AgenticArtifactReference): void;
    onDelete(artifact: AgenticArtifactReference): void;
    onRowClick?(row: PublishedAgenticArtifactTableRow): void;
}

function PublishedAgenticArtifactRow(props: PublishedAgenticArtifactRowProps): ReactNode {
    const { spaceId, row, onRename, onDelete, onRowClick } = props;
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
        if (
            !menuButtonRef.current?.contains(event.target as HTMLElement) &&
            (event.target as HTMLElement).closest('[role="menu"]') == null
        ) {
            const { artifact, publishedByUser } = row;
            onRowClick?.({ artifact, publishedByUser });
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.target === event.currentTarget && (event.code === 'Enter' || event.code === 'Space')) {
            event.preventDefault();
            const { artifact, publishedByUser } = row;
            onRowClick?.({ artifact, publishedByUser });
        }
    };

    return (
        <tr
            className={onRowClick != null ? styles.clickableRow : undefined}
            tabIndex={onRowClick != null ? 0 : undefined}
            onClick={onRowClick != null ? handleRowClick : undefined}
            onKeyDown={onRowClick != null ? handleKeyDown : undefined}
            data-testid={`optimizationReportsTableRow-${row.artifact.artifactId}`}
        >
            <FaroTable.Cell>
                <div className={styles.nameCell}>{row.artifact.name}</div>
            </FaroTable.Cell>
            <FaroTable.Cell>
                <div className={styles.studyDefinitionCell}>{row.artifact.studyDefinitionUsed}</div>
            </FaroTable.Cell>
            <FaroTable.Cell>{getFormattedDateTime(row.artifact.studyRevisionDate)}</FaroTable.Cell>
            <FaroTable.Cell>{getFormattedDateTime(row.artifact.publishedDate)}</FaroTable.Cell>
            <FaroTable.Cell>{row.publishedByUser != null && <FaroAvatar user={row.publishedByUser} />}</FaroTable.Cell>
            <FaroTable.Cell>
                <PublishedAgenticArtifactMenu
                    spaceId={spaceId}
                    artifact={row.artifact}
                    menuButtonRef={menuButtonRef}
                    onRename={() => onRename(row.artifact)}
                    onDelete={() => onDelete(row.artifact)}
                />
            </FaroTable.Cell>
        </tr>
    );
}
